import * as Flashphoner from '@flashphoner/websdk';
import * as RoomApi from '@flashphoner/websdk/src/room-module.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { geolocationOptions } from 'hooks/geolocationOptions ';
import useInterval from 'hooks/useInterval';
import useWatchLocation from 'hooks/useWatchLocation';
import {
  addChat,
  addLocation,
  connectUser,
  disconnectUser,
  getChats,
  startRecording,
  stopRecording,
} from 'services/api-service/center';
import {
  buildStateReport,
  CameraFacing,
  ControlMessage,
  decodeMessage,
  DeviceCaps,
  DeviceState,
  EMPTY_CAPS,
  encodeMessage,
  isControlMessage,
  STATE_REPORT,
  ZoomDirection,
} from 'services/control-protocol';
import {
  CameraSet,
  describeCameraSwitchError,
  getLocalVideoTrack,
  isSwitchUnsupportedError,
  listRearCameras,
  nextZoom,
  readCameraCaps,
  readCurrentZoom,
  readDeviceId,
  resolveCamerasByLabel,
  setTorch,
  setZoom,
  waitForCameraCaps,
} from 'services/media-capability';
import { IStoreState } from 'store/modules';
import { ActionCtor } from 'store/modules/main';
import * as FPUtils from '../modules/fp-utils';
import { isViewerName } from '../modules/hidden-audio';
import { ICE_MEDIA_OPTIONS } from '../modules/ice-config';
import EventRescue from './EventRescue';
import IntroRescue from './IntroRescue';
import IntroVictim from './IntroVictim';
import MapRescue from './MapRescue';
import ModalDuration from './ModalDuration';
import './Rescue.css';

const SESSION_STATUS = Flashphoner.constants.SESSION_STATUS;
const STREAM_STATUS = Flashphoner.constants.STREAM_STATUS;
const ROOM_EVENT = RoomApi.events;
const Browser = Flashphoner.Browser;
const PRELOADER_URL = process.env.PUBLIC_URL + '/media/preloader.mp4';
const maxParticipants = 40;
const CAMERA_SWITCH_MAX_FAILURES = 2;
const REAR_PROBE_WAIT_MS = 8000;
const isAudioSourceName = (name: string) => name.includes('center__') || isViewerName(name);
const isCenterName = (name: string) => name.includes('center__');

interface StyledProps {
  readonly published: boolean;
}
interface ConferenceState {
  session: any;
  sessionStatus: string;
  sessionFailedInfo: string;
  publishStream: null;
  publishStatus: string;
  joinButtonDisabled: boolean;
  serverUrl: string;
  login: string | undefined;
  loginDisabled: boolean;
  publishButtonDisabled: boolean;
  sendButtonDisabled: boolean;
  muteAudioButtonDisabled: boolean;
  muteVideoButtonDisabled: boolean;
  record: boolean;
  recordDisplay: boolean;
  room: any;
  participants: any[];
  chat: any[];
  message: string;
  mutedAudio: boolean;
  mutedVideo: boolean;
}

interface RescueIntroProps {
  agree: boolean;
  duration: boolean;
  room?: string;
  role?: string;
  phone?: string;
  setAgree: (agree: boolean) => void;
  setDuration: (duration: boolean) => void;
}

const RescuePage = ({ agree, duration, room, role, phone, setAgree, setDuration }: RescueIntroProps) => {
  const receivedCommand = useSelector((store: IStoreState) => store.MainReducer.receivedCommand);
  const receivedMessage = useSelector((store: IStoreState) => store.MainReducer.receivedMessage);
  const dispatch = useDispatch();
  const { location, cancelLocationWatch, error } = useWatchLocation(geolocationOptions);
  const [participant, setParticipant] = useState<string | undefined>();
  const [message, setMessage] = useState<any | undefined>();
  const [isRunning, setIsRunning] = useState(false);
  const [isEvent, setIsEvent] = useState(false);
  const [minute, setMinute] = useState(0);
  const minuteRef = useRef(minute);
  const lastReportRef = useRef<string>('');
  const recordingRef = useRef(false);
  const recordingErrorRef = useRef<string | null>(null);
  const camerasRef = useRef<CameraSet | null>(null);
  const facingRef = useRef<CameraFacing>('rear');
  const torchRef = useRef(false);
  const cameraErrorRef = useRef<string | null>(null);
  const cameraFailuresRef = useRef(0);
  const preferredRearIdRef = useRef<string | null>(null);
  const rearProbeRef = useRef<Promise<void> | null>(null);
  const publishStreamRef = useRef<any>(null);
  const controlQueueRef = useRef<Promise<void>>(Promise.resolve());

  const [conference, setConference] = useState<ConferenceState>({
    session: null,
    sessionStatus: '',
    sessionFailedInfo: '',
    publishStream: null,
    publishStatus: '',
    joinButtonDisabled: false,
    serverUrl: 'wss://smartsar.kr/1/ws',
    login: phone,
    loginDisabled: false,
    publishButtonDisabled: false,
    sendButtonDisabled: true,
    muteAudioButtonDisabled: true,
    muteVideoButtonDisabled: true,
    record: false,
    recordDisplay: false,
    room: null,
    participants: [],
    chat: [],
    message: '',
    mutedAudio: true,
    mutedVideo: true,
  });
  const conferenceRef = useRef(conference);
  conferenceRef.current = conference;

  useEffect(() => {
    try {
      Flashphoner.init({});
    } catch (e) {
      console.log(e);
      setConference((prevState) => ({
        ...prevState,
        joinButtonDisabled: true,
        loginDisabled: true,
      }));
    }
    // dispatch(ActionCtor.getRoom(room));

    return () => {
      sendDisConnection();
    };
  }, []);

  useEffect(() => {
    if (agree && role === 'V') {
      createConnection(conference.serverUrl, phone);
    }
  }, [agree]);

  useInterval(() => {
    if (isRunning) {
      sendLocation();
    }
  }, 5000);

  const sendLocation = useCallback(async () => {
    // console.log('Location Change', location, error);
    if (location === undefined) {
      return;
    }
    if (conference.session && conference.room) {
      const participants = conference.room.getParticipants();
      for (let i = 0; i < participants.length; i++) {
        participants[i].sendMessage(encodeURIComponent(JSON.stringify(location)));
      }
      const LocationItems: any = {
        phone: phone,
        roomId: room,
        role: role,
        locateLogs: [
          {
            lat: location.lat,
            lng: location.lng,
            acc: location.acc,
            speed: location.speed,
            heading: location.heading,
          },
        ],
      };
      try {
        const response: any = await addLocation(LocationItems);
        // console.log('sendLocation ', response);
      } catch (error) {
        console.log('sendLocation Error ', error);
      }
    }
  }, [cancelLocationWatch, conference.room, conference.session, error, location, phone, room, role]);

  const onAgree = () => {
    setAgree(true);
    setIsEvent(true);
    if (role === 'R' && duration) {
      createConnection(conference.serverUrl, phone);
    }
  };

  const onUserEvent = () => {
    setIsEvent(true);
    if (role === 'R' && duration) {
      createConnection(conference.serverUrl, phone);
    }
  };

  const onSetDuration = (time: number) => {
    setMinute(time);
    minuteRef.current = time;
    setDuration(true);
    if (role === 'R') {
      createConnection(conference.serverUrl, phone);
    }
  };

  const onJoined = (room) => {
    sendConnection();
    setConference((prevState) => ({
      ...prevState,
      room: room,
      joinButtonDisabled: false,
      loginDisabled: true,
      sendButtonDisabled: false,
      sessionFailedInfo: '',
    }));
  };

  const onLeft = () => {
    sendDisConnection();
    publishStreamRef.current = null;
    setConference((prevState) => ({
      ...prevState,
      publishStream: null,
      joinButtonDisabled: false,
      loginDisabled: false,
      publishButtonDisabled: true,
      sendButtonDisabled: true,
      muteAudioButtonDisabled: true,
      muteVideoButtonDisabled: true,
      room: null,
      participants: [],
      chat: [],
      message: '',
    }));
  };

  const onDisconnected = () => {
    setConference((prevState) => ({
      ...prevState,
      session: null,
    }));
    onLeft();
  };

  const createConnection = (url, username) => {
    const session = conference.session;

    if (session && session.status() === SESSION_STATUS.ESTABLISHED) {
      if (session.getServerUrl() !== url || session.username() !== username) {
        session.on(SESSION_STATUS.DISCONNECTED, () => {
          setConference((prevState) => ({
            ...prevState,
            session: null,
          }));
          createConnection(url, username);
        });
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        session.on(SESSION_STATUS.FAILED, () => {});
        session.disconnect();
      } else {
        joinRoom(session);
      }
    } else {
      // console.log('Create new RoomApi session with url ' + url + ', login ' + username);
      setConference((prevState) => ({
        ...prevState,
        joinButtonDisabled: true,
      }));
      const options = {
        urlServer: url,
        username: username,
        mediaOptions: ICE_MEDIA_OPTIONS,
      };
      RoomApi.connect(options)
        .on(SESSION_STATUS.ESTABLISHED, (session: any) => {
          setConference((prevState) => ({
            ...prevState,
            session: session,
            sessionStatus: SESSION_STATUS.ESTABLISHED,
          }));
          // console.log('Join the room session ' + session);
          joinRoom(session);
        })
        .on(SESSION_STATUS.DISCONNECTED, () => {
          console.warn('[diag] session DISCONNECTED', new Date().toISOString(), { online: navigator.onLine, hidden: document.hidden });
          setConference((prevState) => ({
            ...prevState,
            sessionStatus: SESSION_STATUS.DISCONNECTED,
          }));
          onDisconnected();
        })
        .on(SESSION_STATUS.FAILED, () => {
          console.warn('[diag] session FAILED', new Date().toISOString(), { online: navigator.onLine, hidden: document.hidden });
          setConference((prevState) => ({
            ...prevState,
            sessionStatus: SESSION_STATUS.FAILED,
          }));
          onDisconnected();
        });
    }
  };

  const joinRoom = (session) => {
    const roomName = getRoomName();
    const record = conference.record;

    // console.log('Join the room ' + roomName + ', session ' + session);
    session
      .join({ name: roomName, record: record })
      .on(ROOM_EVENT.STATE, (room) => {
        const roomParticipants = room.getParticipants();
        const participantsNumber = roomParticipants.length;
        // console.log('Current number of participants in the room: ' + participantsNumber);
        const memberCount = roomParticipants.filter((p) => !p.name().startsWith('viewer__')).length;
        if (memberCount >= maxParticipants) {
          console.warn('Current room is full');
          setConference((prevState) => ({
            ...prevState,
            sessionFailedInfo: 'Current room is full',
          }));
          room.leave().then(
            () => {
              onLeft();
            },
            () => {
              onLeft();
            }
          );
          return false;
        }
        if (participantsNumber > 0) {
          let chatState = 'participants: ';
          for (let i = 0; i < participantsNumber; i++) {
            installParticipant(roomParticipants[i]);
            chatState += roomParticipants[i].name();
            if (i < participantsNumber - 1) {
              chatState += ',';
            }
          }
        }
        if (Browser.isSafariWebRTC()) {
          Flashphoner.playFirstVideo(document.getElementById('localDisplay'), true, PRELOADER_URL).then(() => {
            if (role === 'R') {
              publishLocalMedia(room);
            }
            onJoined(room);
          });
          return;
        }
        if (role === 'R') {
          publishLocalMedia(room);
        }
        onJoined(room);
      })
      .on(ROOM_EVENT.JOINED, (participant) => {
        // console.log('JOINED: ' + participant);
        installParticipant(participant);
      })
      .on(ROOM_EVENT.LEFT, (participant) => {
        // console.log('LEFT: ' + participant.name());
        if (isAudioSourceName(participant.name())) {
          removeParticipant(participant);
        }
      })
      .on(ROOM_EVENT.PUBLISHED, (participant) => {
        // console.log('PUBLISHED: ' + participant.name());
        if (isAudioSourceName(participant.name()) && role === 'R') {
          playParticipantsStream(participant);
        }
      })
      .on(ROOM_EVENT.FAILED, (room, info) => {
        toast('확인 후 재 연결바랍니다.', { type: 'error' });
        setAgree(false);
        session.disconnect();
        setConference((prevState) => ({
          ...prevState,
          sessionFailedInfo: info,
        }));
      })
      .on(ROOM_EVENT.MESSAGE, (message) => {
        if (message.from && message.text) {
          dispatch(ActionCtor.setCommand(message));
        }
      });
  };

  const parseCommand = (message: any) => {
    try {
      handleCommand(message);
    } catch (error) {
      console.log('parseCommand Error ', error, message);
    }
  };

  const enqueueControl = (task: () => Promise<void> | void): Promise<void> => {
    const run = async () => {
      const probe = rearProbeRef.current;
      if (probe !== null) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const finished = await Promise.race([
          probe.then(
            () => true,
            () => true
          ),
          new Promise<boolean>((resolve) => {
            timer = setTimeout(() => resolve(false), REAR_PROBE_WAIT_MS);
          }),
        ]);
        clearTimeout(timer);
        if (!finished && rearProbeRef.current === probe) {
          console.log('후면 카메라 탐색이 끝나지 않아 기다리지 않고 제어를 처리합니다.');
          rearProbeRef.current = null;
        }
      }
      await task();
    };
    const next = controlQueueRef.current.then(run).catch((error) => console.log('control task Error ', error));
    controlQueueRef.current = next;
    return next;
  };

  const handleCommand = (message: any) => {
    const received = decodeMessage(message.text);
    if (isControlMessage(received)) {
      if (isCenterName(message.from.name())) {
        enqueueControl(() => applyControl(received));
      }
    } else if (received.type === 'Video') {
      onMuteVideoClick();
    } else if (received.type === 'Audio') {
      onMuteAudioClick();
    } else if (received.type === 'Expire') {
      // Expire Room
      onExpire();
      dispatch(ActionCtor.getRoom(room));
    } else if (received.type === 'Information') {
      // Accident Information
      dispatch(ActionCtor.getRoom(room));
      // Update Map
    } else if (received.type === 'Chat') {
      // Update Chat Message
      dispatch(ActionCtor.addMessage(JSON.parse(decodeURIComponent(message.text))));
    } else if (received.type === 'Update') {
      // console.log('MESSAGE: ' + message.from.name(), JSON.parse(decodeURIComponent(message.text)));
    } else {
      // Location Message
      dispatch(ActionCtor.updateLocations([message.from.name(), JSON.parse(decodeURIComponent(message.text))]));
      setParticipant(message.from.name());
      setMessage(message);
    }
  };

  useEffect(() => {
    if (receivedCommand != undefined) {
      parseCommand(receivedCommand);
    }
  }, [receivedCommand]);

  useEffect(() => {
    if (conference.room && conference.publishStream) {
      sendDeviceState(undefined, true);
    }
  }, [conference.room, conference.publishStream]);

  const addMessage = (message) => {
    // console.log('addMessage' + message);
    setConference((prevState) => ({
      ...prevState,
      chat: [...prevState.chat, message],
    }));
  };

  const installParticipant = (roomParticipant) => {
    const name = roomParticipant.name();

    // console.log('Add participant ' + name);
    if (isAudioSourceName(name) && role === 'R') {
      playParticipantsStream(roomParticipant);
    }
    setConference((prevState) => {
      if (prevState.participants.length >= maxParticipants) {
        console.warn('More than ' + maxParticipants + ' participants, ignore participant ' + name);
        return prevState;
      }
      return {
        ...prevState,
        participants: [
          ...prevState.participants,
          {
            name: name,
            roomParticipant: roomParticipant,
            stream: null,
          },
        ],
      };
    });
  };

  const onMediaPublished = (stream) => {
    toast('스트림에 연결되었습니다.', { type: 'success' });
    publishStreamRef.current = stream;
    recordingRef.current = false;
    if (role === 'R' && stream) {
      stream.muteAudio();
      stream.muteVideo();
    }
    setConference((prevState) => ({
      ...prevState,
      publishStream: stream,
      publishButtonDisabled: false,
      muteAudioButtonDisabled: false,
      muteVideoButtonDisabled: false,
    }));
    sendDeviceState(stream);
    if (role === 'R') {
      rearProbeRef.current = resolveCamerasIfNeeded()
        .then(() => ensureTorchCapableRear(stream))
        .finally(() => {
          rearProbeRef.current = null;
          reportWhenCapsSettle(stream);
        });
    } else {
      reportWhenCapsSettle(stream);
    }
  };

  const reportWhenCapsSettle = async (stream: any) => {
    const track = getLocalVideoTrack();
    if (track !== null) {
      await waitForCameraCaps(track);
    }
    sendDeviceState(stream, true);
  };

  const ensureTorchCapableRear = async (stream: any) => {
    const track = getLocalVideoTrack();
    if (!stream || track === null) {
      return;
    }
    const originalId = readDeviceId(track);
    if ((await waitForCameraCaps(track)).torch) {
      preferredRearIdRef.current = originalId;
      return;
    }
    const candidates = (await listRearCameras()).filter((camera) => camera.deviceId !== originalId);
    for (const camera of candidates) {
      try {
        await stream.switchCam(camera.deviceId);
      } catch (error) {
        console.log('후면 카메라 탐색 실패 ', camera.label, error);
        continue;
      }
      const probed = getLocalVideoTrack();
      if (probed !== null && (await waitForCameraCaps(probed)).torch) {
        preferredRearIdRef.current = camera.deviceId;
        if (camerasRef.current !== null) {
          camerasRef.current = { ...camerasRef.current, rear: camera.deviceId };
        }
        return;
      }
    }
    const current = getLocalVideoTrack();
    if (originalId !== null && current !== null && readDeviceId(current) !== originalId) {
      try {
        await stream.switchCam(originalId);
      } catch (error) {
        console.log('원래 후면 카메라 복귀 실패 ', error);
      }
    }
  };

  const resolveCameras = async () => {
    const track = getLocalVideoTrack();
    const currentId = track !== null ? readDeviceId(track) : null;
    const knownRear = camerasRef.current !== null ? camerasRef.current.rear : null;
    const rearId = preferredRearIdRef.current ?? (facingRef.current === 'rear' ? currentId : knownRear);
    camerasRef.current = await resolveCamerasByLabel(rearId);
    cameraFailuresRef.current = 0;
    cameraErrorRef.current = null;
  };

  const resolveCamerasIfNeeded = async () => {
    if (camerasRef.current !== null && camerasRef.current.canSwitch) {
      return;
    }
    await resolveCameras();
    sendDeviceState(undefined, true);
  };

  const onMediaStopped = () => {
    const session = conference.session;
    let disablePublish = false;

    if (!session || session.getRooms().length === 0) {
      disablePublish = true;
    }
    toast('스트림이 해제되었습니다.', { type: 'error' });
    publishStreamRef.current = null;
    recordingRef.current = false;
    setConference((prevState) => ({
      ...prevState,
      publishStream: null,
      publishButtonDisabled: disablePublish,
      muteAudioButtonDisabled: true,
      muteVideoButtonDisabled: true,
    }));
  };

  const publishLocalMedia = (room) => {
    const preferredRear = preferredRearIdRef.current;
    const constraints = {
      audio: true,
      video: {
        ...(preferredRear ? { deviceId: { exact: preferredRear } } : { facingMode: 'environment' }),
        width: 1280,
        height: 720,
        frameRate: 30,
        minBitrate: 800,
        maxBitrate: 2500,
      },
    };
    facingRef.current = 'rear';
    torchRef.current = false;
    cameraErrorRef.current = null;
    cameraFailuresRef.current = 0;
    const display = document.getElementById('localDisplay');

    setConference((prevState) => ({
      ...prevState,
      publishButtonDisabled: true,
    }));
    const diagStartedAt = Date.now(); // [diag] 임시 진단 로그
    console.log('[diag] publish start', new Date().toISOString(), phone);
    room
      .publish({
        display: display,
        constraints: constraints,
        videoContentHint: 'motion',
        record: role === 'V', // true for record
        receiveVideo: false,
        receiveAudio: false,
      })
      .on(STREAM_STATUS.FAILED, (stream) => {
        console.warn('Local stream failed!');
        console.warn('[diag] publish FAILED', new Date().toISOString(), {
          name: stream.name(),
          info: stream.getInfo(),
          errorInfo: stream.getErrorInfo(),
          elapsedSec: (Date.now() - diagStartedAt) / 1000,
          online: navigator.onLine,
          hidden: document.hidden,
        });
        setConference((prevState) => ({
          ...prevState,
          publishStatus: STREAM_STATUS.FAILED,
        }));
        onMediaStopped();
      })
      .on(STREAM_STATUS.PUBLISHING, (stream) => {
        console.log('[diag] publish PUBLISHING', new Date().toISOString(), stream.name());
        setConference((prevState) => ({
          ...prevState,
          publishStatus: STREAM_STATUS.PUBLISHING,
        }));
        onMediaPublished(stream);
      })
      .on(STREAM_STATUS.UNPUBLISHED, (stream) => {
        console.warn('[diag] publish UNPUBLISHED', new Date().toISOString(), {
          info: stream.getInfo(),
          elapsedSec: (Date.now() - diagStartedAt) / 1000,
        });
        setConference((prevState) => ({
          ...prevState,
          publishStatus: STREAM_STATUS.UNPUBLISHED,
        }));
        onMediaStopped();
      });
  };

  const removeParticipant = (roomParticipant) => {
    const participants: any = conference.participants;
    const name = roomParticipant.name();
    // console.log('removeParticipant ' + name);
    for (let i = 0; i < participants.length; i++) {
      if (participants[i].roomParticipant.name() === name) {
        // console.log('Remove participant ' + name);
        stopParticipantStream(participants[i].stream);
        // setState(
        //   update(conference, {
        //     participants: {
        //       $splice: [[i, 1]],
        //     },
        //   })
        // );
        break;
      }
    }
  };

  const stopParticipantStream = (stream) => {
    // console.log('stopParticipantStream Stopping stream ' + stream);
    if (stream && FPUtils.isPlaying(stream.status())) {
      // console.log('Stopping stream ' + stream.name());
      stream.stop();
    }
  };

  const playStream = (stream, remoteVideo, name) => {
    stream.play(remoteVideo).on(STREAM_STATUS.PLAYING, (playingStream) => {
      const video = document.getElementById(playingStream.id());
      if (video) {
        video.addEventListener('resize', (event) => {
          FPUtils.resizeVideo(event.target);
        });
      }
    });
  };

  const playParticipantsStream = (roomParticipant) => {
    // console.log('playParticipantsStream: ' + roomParticipant);
    let remoteVideo: HTMLElement | null = null;
    let stream = null;
    const name = roomParticipant.name();

    if (roomParticipant.getStreams().length > 0) {
      stream = roomParticipant.getStreams()[0];
      // remoteVideo = document.getElementById(name);
      remoteVideo = document.createElement('div');
      if (stream && remoteVideo) {
        if (Browser.isSafariWebRTC()) {
          Flashphoner.playFirstVideo(remoteVideo, false, PRELOADER_URL).then(() => {
            playStream(stream, remoteVideo, name);
          });
          return;
        }
        Flashphoner.playFirstSound();
        playStream(stream, remoteVideo, name);
      }
    }
  };

  const getRoomName = () => {
    return room;
  };

  const onExpire = () => {
    const room = conference.room;
    const stream: any = conference.publishStream;
    if (room === null) {
      createConnection(conference.serverUrl, phone);
    } else {
      if (stream) {
        stream.stop();
      }
      setConference((prevState) => ({ ...prevState, joinButtonDisabled: true }));
      room.leave().then(
        () => {
          onLeft();
        },
        () => {
          onLeft();
        }
      );
    }
  };

  const onPublishClick = () => {
    const stream: any = conference.publishStream;
    const room = conference.room;

    if (!room) return;
    setConference((prevState) => ({
      ...prevState,
      publishButtonDisabled: true,
    }));
    if (!stream) {
      publishLocalMedia(room);
      if (role === 'V') {
        const participants = room.getParticipants();
        for (let i = 0; i < participants.length; i++) {
          if (isAudioSourceName(participants[i].name())) {
            playParticipantsStream(participants[i]);
          }
        }
      }
      setConference((prevState) => ({
        ...prevState,
        mutedVideo: false,
      }));
    } else {
      stream.stop();
      if (role === 'V') {
        // console.log('onPublishClick Stopping stream ' + stream.name());
        const participants = room.getParticipants();
        for (let i = 0; i < participants.length; i++) {
          if (isAudioSourceName(participants[i].name())) {
            stopParticipantStream(participants[i].stream);
          }
        }
      }
      setConference((prevState) => ({
        ...prevState,
        mutedVideo: true,
      }));
      // Send Message Update List
      onSendUpdate();
    }
  };

  const onSendUpdate = async () => {
    if (conference.session && conference.room) {
      const participants = conference.room.getParticipants();
      const UpdateItems: any = {
        type: 'Update',
        roomId: room,
        chatId: phone,
        chatNm: phone,
        msg: '',
        chatType: role === 'V' ? 1 : 2,
      };
      for (let i = 0; i < participants.length; i++) {
        participants[i].sendMessage(encodeURIComponent(JSON.stringify(UpdateItems)));
      }
    }
  };

  const isMutedAudio = () => {
    const stream: any = publishStreamRef.current;
    if (!stream) return false;
    return stream.isAudioMuted();
  };

  const applyAudioEnabled = (enabled: boolean): boolean => {
    const stream: any = publishStreamRef.current;

    if (!stream) {
      toast('스트림 연결 후 사용하세요.', { type: 'error' });
      return false;
    }
    if (enabled) {
      stream.unmuteAudio();
    } else {
      stream.muteAudio();
    }
    setConference((prevState) => ({
      ...prevState,
      mutedAudio: !enabled,
    }));
    return true;
  };

  const onMuteAudioClick = () => {
    enqueueControl(() => {
      const stream: any = publishStreamRef.current;

      if (!stream) {
        toast('스트림 연결 후 사용하세요.', { type: 'error' });
        return;
      }
      applyAudioEnabled(stream.isAudioMuted());
      sendDeviceState(undefined, true);
    });
  };

  const readDeviceCaps = (): DeviceCaps => {
    const track = getLocalVideoTrack();
    if (track === null) {
      return EMPTY_CAPS;
    }
    const caps = readCameraCaps(track);
    return {
      torch: caps.torch,
      camera: camerasRef.current !== null && camerasRef.current.canSwitch,
      zoom: caps.zoom,
    };
  };

  const applyTorch = async (on: boolean) => {
    const track = getLocalVideoTrack();
    if (track === null) {
      return;
    }
    const applied = await setTorch(track, on);
    torchRef.current = applied ? on : false;
  };

  const applyCameraFacing = async (facing: CameraFacing) => {
    const stream: any = publishStreamRef.current;
    const cameras = camerasRef.current;
    if (!stream || cameras === null) {
      return;
    }
    const targetId = facing === 'front' ? cameras.front : cameras.rear;
    if (!targetId || facingRef.current === facing) {
      return;
    }
    const previousFacing = facingRef.current;
    torchRef.current = false;
    try {
      await stream.switchCam(targetId);
      facingRef.current = facing;
      cameraFailuresRef.current = 0;
      cameraErrorRef.current = null;
      reportWhenCapsSettle(stream);
    } catch (error) {
      console.log('카메라 전환 실패 ', error);
      cameraFailuresRef.current += 1;
      cameraErrorRef.current = describeCameraSwitchError(error);
      const unsupported = isSwitchUnsupportedError(error) || cameraFailuresRef.current >= CAMERA_SWITCH_MAX_FAILURES;
      if (unsupported && camerasRef.current !== null) {
        camerasRef.current = { ...camerasRef.current, canSwitch: false };
      }
      await recoverLocalVideo(stream, previousFacing);
    }
  };

  const recoverLocalVideo = async (stream: any, facing: CameraFacing) => {
    const track = getLocalVideoTrack();
    if (track !== null && track.readyState === 'live') {
      return;
    }
    const cameras = camerasRef.current;
    const fallbackId = cameras === null ? null : facing === 'front' ? cameras.front : cameras.rear;
    if (!fallbackId) {
      cameraErrorRef.current = '카메라를 다시 열지 못했습니다. 카메라를 껐다 켜 주세요.';
      return;
    }
    try {
      await stream.switchCam(fallbackId);
      facingRef.current = facing;
      reportWhenCapsSettle(stream);
    } catch (error) {
      console.log('카메라 복구 실패 ', error);
      cameraErrorRef.current = '카메라를 다시 열지 못했습니다. 카메라를 껐다 켜 주세요.';
    }
  };

  const applyZoomStep = async (direction: ZoomDirection) => {
    const track = getLocalVideoTrack();
    if (track === null) {
      return;
    }
    const caps = readCameraCaps(track);
    if (caps.zoom === null) {
      return;
    }
    const current = readCurrentZoom(track);
    const target = nextZoom(current, caps.zoom, direction);
    if (current !== null && target === current) {
      return;
    }
    await setZoom(track, target);
  };

  const readDeviceState = (target?: any): DeviceState | null => {
    const stream: any = target || publishStreamRef.current;
    if (!stream) {
      return null;
    }
    const track = getLocalVideoTrack();
    return {
      video: !stream.isVideoMuted(),
      audio: !stream.isAudioMuted(),
      torch: torchRef.current,
      facing: facingRef.current,
      zoom: track !== null ? readCurrentZoom(track) : null,
      recording: recordingRef.current,
      recordingError: recordingErrorRef.current,
      cameraError: cameraErrorRef.current,
    };
  };

  const sendDeviceState = (target?: any, force?: boolean) => {
    const { session, room: joinedRoom } = conferenceRef.current;
    if (!session || !joinedRoom || phone === undefined) {
      return;
    }
    const state = readDeviceState(target);
    if (state === null) {
      return;
    }
    try {
      const payload = encodeMessage(buildStateReport(phone, readDeviceCaps(), state, room));
      if (!force && payload === lastReportRef.current) {
        return;
      }
      lastReportRef.current = payload;
      const participants = joinedRoom.getParticipants();
      for (let i = 0; i < participants.length; i++) {
        if (isCenterName(participants[i].name())) {
          participants[i].sendMessage(payload);
        }
      }
    } catch (error) {
      console.log('sendDeviceState Error ', error);
    }
  };

  const applyControl = async (command: ControlMessage) => {
    if (command.cmd === STATE_REPORT) {
      return;
    }
    const stream: any = publishStreamRef.current;
    const videoOn = stream ? !stream.isVideoMuted() : false;
    const needsCamera = command.cmd === 'torch' || command.cmd === 'camera' || command.cmd === 'zoom';

    try {
      if (needsCamera && !videoOn) {
        // Pass
      } else {
        switch (command.cmd) {
          case 'video':
            await setVideoWithRecording(command.value === true);
            break;
          case 'audio':
            applyAudioEnabled(command.value === true);
            break;
          case 'torch':
            await applyTorch(command.value === true);
            break;
          case 'camera':
            await applyCameraFacing(command.value === 'front' ? 'front' : 'rear');
            break;
          case 'zoom':
            await applyZoomStep(command.value === 'in' ? 'in' : 'out');
            break;
          case 'caps':
            if (stream) {
              await resolveCameras();
            }
            break;
          default:
            break;
        }
      }
    } finally {
      sendDeviceState(undefined, true);
    }
  };

  const isMutedvideo = () => {
    const stream: any = publishStreamRef.current;
    if (!stream) return true;
    return stream.isVideoMuted();
  };

  const applyVideoEnabled = (enabled: boolean): boolean => {
    const stream: any = publishStreamRef.current;

    if (!stream) {
      toast('스트림 연결 후 사용하세요.', { type: 'error' });
      return false;
    }
    if (enabled) {
      stream.unmuteVideo();
    } else {
      stream.muteVideo();
    }
    setConference((prevState) => ({
      ...prevState,
      mutedVideo: !enabled,
    }));
    return true;
  };

  const setVideoWithRecording = async (enabled: boolean) => {
    const stream: any = publishStreamRef.current;

    if (!stream) {
      toast('스트림 연결 후 사용하세요.', { type: 'error' });
      return;
    }
    if (stream.isVideoMuted() === !enabled) {
      return;
    }
    if (enabled) {
      if (applyVideoEnabled(true)) {
        // Record Start
        setRecording(true, stream.id());
      }
    } else {
      if (torchRef.current) {
        await applyTorch(false);
      }
      // Record Stop
      setRecording(false, stream.id());
      applyVideoEnabled(false);
    }
  };

  const onMuteVideoClick = () => {
    enqueueControl(async () => {
      const stream: any = publishStreamRef.current;

      if (!stream) {
        toast('스트림 연결 후 사용하세요.', { type: 'error' });
        return;
      }
      await setVideoWithRecording(stream.isVideoMuted());
      sendDeviceState(undefined, true);
    });
  };

  const setRecording = async (enabled: boolean, id: string) => {
    if (!id || recordingRef.current === enabled) {
      return;
    }
    recordingRef.current = enabled;
    const formData = new FormData();
    formData.append('mediaSessionId', id);
    try {
      if (enabled) {
        await startRecording(formData);
      } else {
        await stopRecording(formData);
      }
      recordingErrorRef.current = null;
    } catch (error: any) {
      recordingRef.current = !enabled;
      recordingErrorRef.current = error?.response?.data?.message || error?.message || '녹화 제어에 실패했습니다.';
      console.log(
        'setRecording Error ',
        enabled ? 'startRecording' : 'stopRecording',
        'mediaSessionId=' + id,
        'status=' + error?.response?.status,
        error?.response?.data,
        error
      );
    } finally {
      sendDeviceState(undefined, true);
      if (!enabled) {
        // Send Message Update List
        onSendUpdate();
      }
    }
  };

  const onSendChat = async (msg: string) => {
    // console.log('onSendChat ', msg);
    if (conference.session && conference.room) {
      const participants = conference.room.getParticipants();
      const ChatItems: any = {
        type: 'Chat',
        roomId: room,
        chatId: phone,
        chatNm: phone,
        msg,
        chatType: role === 'V' ? 1 : 2,
      };
      for (let i = 0; i < participants.length; i++) {
        // console.log('onSendChat Message', participants);
        participants[i].sendMessage(encodeURIComponent(JSON.stringify(ChatItems)));
      }
      try {
        const chatType = role === 'V' ? 1 : 2;
        await addChat(room, phone, phone, msg, chatType);
      } catch (error) {
        console.log('addChat Error ', error);
      }
      // Update Chat
      addMessage(ChatItems);
    }
  };

  const sendConnection = async () => {
    if (room == undefined || phone === undefined || role === undefined) {
      return;
    }
    try {
      const timeToUse = minuteRef.current;
      const response: any = await connectUser(room, phone, role, timeToUse);
      setIsRunning((prevIsRunning) => !prevIsRunning);
      dispatch(ActionCtor.getLocations(room));
      // dispatch(ActionCtor.getRoom(room));
      // console.log('sendConnection ', response);
    } catch (error) {
      console.log('sendConnection Error ', error);
    }
    try {
      const response: any = await getChats(room);
      setConference((prevState) => ({
        ...prevState,
        chat: [...prevState.chat, ...response.data],
      }));
    } catch (error) {
      // console.log('getChats Error ', error);
    }
  };

  const sendDisConnection = async () => {
    setIsRunning((prevIsRunning) => !prevIsRunning);
    if (room == undefined || phone === undefined || role === undefined) {
      return;
    }
    try {
      const response: any = await disconnectUser(room, phone, role);
      // console.log('sendDisConnection ', response);
    } catch (error) {
      console.log('sendDisConnection Error ', error);
    }
  };

  useEffect(() => {
    if (receivedMessage !== undefined) {
      addMessage(receivedMessage);
    }
  }, [receivedMessage]);

  if (!agree && role === 'V') {
    return <IntroVictim onClick={onAgree} />;
  }

  return (
    <RescuePageWrap>
      <VideoWrap published={conference.mutedVideo} id="localDisplay"></VideoWrap>
      <MapWrap published={conference.mutedVideo}>
        <MapRescue
          role={role}
          phone={phone}
          participant={participant}
          location={location}
          message={message}
        ></MapRescue>
      </MapWrap>
      <PlayWrap id="center" />

      {!agree && role === 'R' && <IntroRescue onClick={onAgree} />}

      {agree && role === 'V' && (
        <PublishButton onClick={onPublishClick}>
          {conference.publishStatus === STREAM_STATUS.PUBLISHING ? (
            <img src={process.env.PUBLIC_URL + '/images/btn_stop_record.svg'} alt="stop record" />
          ) : (
            <img src={process.env.PUBLIC_URL + '/images/btn_start_record.svg'} alt="start record" />
          )}
        </PublishButton>
      )}

      {agree && role === 'R' && !isEvent && <EventRescue onClick={onUserEvent} />}

      {agree && role === 'R' && isEvent && (
        <>
          <VideoButton onClick={onMuteVideoClick}>
            {conference.mutedVideo ? (
              <img src={process.env.PUBLIC_URL + '/images/icon_video_muted.svg'} alt="video muted" />
            ) : (
              <img src={process.env.PUBLIC_URL + '/images/icon_video.svg'} alt="video" />
            )}
          </VideoButton>
          <AudioButton onClick={onMuteAudioClick}>
            {conference.mutedAudio ? (
              <img src={process.env.PUBLIC_URL + '/images/icon_audio_stop.svg'} alt="audio muted" />
            ) : (
              <img src={process.env.PUBLIC_URL + '/images/icon_audio_start.svg'} alt="audio" />
            )}
          </AudioButton>
          {!duration && <ModalDuration onClose={onSetDuration} />}
        </>
      )}
    </RescuePageWrap>
  );
};

const RescuePageWrap = styled.div`
  width: 100%;
  height: 100%;
`;
const VideoWrap = styled.div<StyledProps>`
  width: 100%;
  height: ${(props) => (!props.published ? '50%' : '0%')};
  background: black;
`;
const MapWrap = styled.div<StyledProps>`
  width: 100%;
  height: ${(props) => (!props.published ? '50%' : '100%')};
`;
const PlayWrap = styled.div`
  width: 1px;
  height: 1px;
  background: transparent;
`;
const VideoButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  border-radius: 10px;
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 15px;
  color: #ffffff;
  background: transparent;
  border: none;
  cursor: pointer;
  white-space: pre-line;
  z-index: 1;
  padding: 0;
`;
const AudioButton = styled.button`
  position: absolute;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 10px;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: #ffffff;
  background: transparent;
  border: none;
  cursor: pointer;
  white-space: pre-line;
  z-index: 1;
  padding: 0;
`;
const PublishButton = styled(AudioButton)`
  height: 46px;
  bottom: 20px;
`;

export default RescuePage;
